import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCount, formatDelta, prettyQuarter } from '@/lib/format';
import { colorForRegion } from '@/lib/titleCharts';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary, Emph } from './ChartSummary';
import { QuarterTick, formatAxisCount, useQuarterWindow, profileChartStyles as s } from './ProfileSection';

interface HistoryPoint {
  quarter: string;
  subscribers: number;
}

export interface SubscribersByRegionChartProps {
  platform: string;
  byRegion: Partial<Record<string, HistoryPoint[]>>;
  /** Regions to stack — all four, or the picked one. */
  regions: string[];
}

/** Subscribers stacked by region over time, with the fastest-growing region named. */
export function SubscribersByRegionChart({ platform, byRegion, regions }: SubscribersByRegionChartProps) {
  const allPoints = useMemo(() => {
    const quarters = [...new Set(regions.flatMap((r) => (byRegion[r] ?? []).map((h) => h.quarter)))].sort();
    return quarters.map((q) => {
      const p: Record<string, number | string> = { quarter: q, label: prettyQuarter(q) };
      for (const r of regions) p[r] = byRegion[r]?.find((h) => h.quarter === q)?.subscribers ?? 0;
      return p;
    });
  }, [byRegion, regions.join('|')]);

  const [[lo, hi], setRange] = useQuarterWindow(allPoints.length);
  const points = allPoints.slice(lo, hi + 1);
  const colors = useResolvedColors(regions.map(colorForRegion));

  const first = points[0];
  const last = points[points.length - 1];
  const total = (p?: Record<string, number | string>) => (p ? regions.reduce((a, r) => a + Number(p[r] ?? 0), 0) : 0);
  const growth = regions
    .map((r) => ({ r, g: first && Number(first[r]) ? ((Number(last[r]) - Number(first[r])) / Number(first[r])) * 100 : undefined }))
    .filter((x): x is { r: string; g: number } => x.g !== undefined)
    .sort((a, b) => b.g - a.g);
  const fastest = regions.length > 1 ? growth[0] : undefined;

  return (
    <>
      <ChartSummary className={s.summary}>
        {last ? (
          <>
            <Emph>{platform}</Emph> had <Emph>{formatCount(total(last))}</Emph> subscribers in{' '}
            <Emph>{String(last.label)}</Emph>
            {first && first !== last && total(first) ? (
              <>
                , <Emph>{formatDelta(((total(last) - total(first)) / total(first)) * 100)}</Emph> since{' '}
                {String(first.label)}
              </>
            ) : null}
            {fastest && first !== last ? (
              <>
                . <Emph>{fastest.r}</Emph> grew fastest (<Emph>{formatDelta(fastest.g)}</Emph>)
              </>
            ) : null}
            .
          </>
        ) : (
          '—'
        )}
      </ChartSummary>
      <div className={s.body}>
        {allPoints.length > 1 && (
          <QuarterRangeSlider
            count={allPoints.length}
            value={[lo, hi]}
            onChange={setRange}
            labels={allPoints.map((p) => String(p.label))}
          />
        )}
        {points.length ? (
          <>
            <div className={s.plot}>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleLeft}`}>Number of Subscribers</span>
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={points} margin={{ top: 16, right: 28, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={{ stroke: 'var(--border-default)' }}
                    tickLine={false}
                    interval={points.length > 16 ? 'preserveStartEnd' : 0}
                    height={46}
                    tick={QuarterTick}
                  />
                  <YAxis
                    className={s.axis}
                    axisLine={false}
                    tickLine={false}
                    width={64}
                    tickFormatter={(v) => formatAxisCount(Number(v))}
                  />
                  <Tooltip
                    cursor={{ stroke: 'var(--border-strong)' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as Record<string, number | string>;
                      return (
                        <ChartTooltip
                          title={label}
                          rows={regions
                            .slice()
                            .reverse()
                            .map((r) => ({ label: r, value: formatCount(Number(p[r])), color: colors[regions.indexOf(r)] }))}
                          totals={regions.length > 1 ? [{ label: 'Total', value: formatCount(total(p)) }] : []}
                        />
                      );
                    }}
                  />
                  {regions.map((r, i) => (
                    <Area
                      key={r}
                      dataKey={r}
                      stackId="subs"
                      stroke={colors[i]}
                      fill={colors[i]}
                      fillOpacity={0.55}
                      strokeWidth={1.5}
                      isAnimationActive={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
              <span />
            </div>
            <ChartLegend items={regions.map((r, i) => ({ label: r, color: colors[i] }))} />
          </>
        ) : (
          <div className={s.empty}>No subscriber data available</div>
        )}
      </div>
    </>
  );
}
